import { store } from "store";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { AuthEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import { CompanyService } from "services";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { TimeReportingFormTypes } from "models/formTypes";
import { MonthShortConfig } from "utils/constants/dateConstants";

function useTimeReportingService() {
  const { apiClientInstance } = useAPIClientHelper();

  const hasCreatePermissions = () => {
    const isInvoiceAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.InvoicesAdmin);
    return CompanyService.isTimeReportingEnabled() && isInvoiceAdmin;
  };

  const hasEditPermissions = () => {
    return hasCreatePermissions() || isProjectManager();
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useTimeReportingPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isManager = timeReportingService.isProjectManager();
   *
   * @example
   * // New way (recommended):
   * function useTimeReportingPermissions() {
   *   const loggedInUserIsProjectManager = useSelector((state: AppState) => state.auth.selectedCompany.loggedInUserIsProjectManager);
   *
   *   const isProjectManager = () => {
   *     return loggedInUserIsProjectManager;
   *   };
   *
   *   return { isProjectManager };
   * }
   *
   * // In component:
   * const { isProjectManager } = useTimeReportingPermissions();
   * const isManager = isProjectManager();
   */
  const isProjectManager = () => {
    const state = store.getState();
    return state.auth.selectedCompany.loggedInUserIsProjectManager;
  };

  const getReport = async (filter: TimeReportingFormTypes.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportClient.Report(reportToAPIFilter(filter));
  };

  const getDueMonthReport = async () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).TimeReportClient.GetDueMonthReport();
  };

  const reportToAPIFilter = (formFilter?: TimeReportingFormTypes.Filter): ApiModel.TimeReportReportFilter => {
    const { fromDate, toDate } = getFilterDateRange(formFilter);

    return {
      personId: formFilter?.personId,
      businessUnitId: formFilter?.businessUnitId,
      fromDate: fromDate,
      toDate: toDate,
      projectId: formFilter?.projectId,
      excludeInvoiced: formFilter?.excludeInvoiced ?? false,
      categoryId: formFilter?.categoryId,
      onlyIncludeHourlyIndividualBillingModel: formFilter?.onlyIncludeHourlyIndividualBillingModel ?? false,
      includeAllEmployees: true,
      includeOnlyDeviations: !!formFilter?.includeOnlyDeviations,
    };
  };

  const filter = async (employeeId: number, start: Date, end: Date) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportClient.Filter({
        employeeId: employeeId,
        startDate: start,
        endDate: end,
      } as ApiModel.TimeReportFilter)
      .then((res) => res.data);
  };

  const getFilterDateRange = (formFilter?: TimeReportingFormTypes.Filter) => {
    let fromDate: Date | undefined;
    let toDate: Date | undefined;

    if (formFilter?.period != null) {
      fromDate = DateHelper.resetTime(DateHelper.getFirstDayOfMonth(formFilter?.period));
      toDate = DateHelper.resetTime(DateHelper.getLastDayOfMonth(formFilter?.period));
    } else {
      fromDate = formFilter?.dateInterval?.start;

      toDate = formFilter?.dateInterval?.end == null && fromDate ? DateHelper.today() : formFilter?.dateInterval?.end;
    }

    return { fromDate, toDate };
  };

  const exportReport = async (filter: TimeReportingFormTypes.Filter, projectId?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportClient.Export(exportToAPIFilter(filter, projectId))
      .then((res) => res.data);
  };

  const exportToAPIFilter = (
    formFilter?: TimeReportingFormTypes.Filter,
    projectId?: string
  ): ApiModel.TimeReportReportFilter => {
    const { fromDate, toDate } = getFilterDateRange(formFilter);

    return {
      personId: formFilter?.personId,
      businessUnitId: formFilter?.businessUnitId,
      fromDate: fromDate,
      toDate: toDate,
      projectId: projectId,
      excludeInvoiced: formFilter?.excludeInvoiced ?? false,
      categoryId: formFilter?.categoryId,
      onlyIncludeHourlyIndividualBillingModel: !!formFilter?.onlyIncludeHourlyIndividualBillingModel,
      includeAllEmployees: true,
      includeOnlyDeviations: !!formFilter?.includeOnlyDeviations,
    };
  };

  const mergeWeeklyTimeReport = async (timeReportData: ApiModel.TimeReportMetaDataWithPeriods) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportClient.MergeTimeReport(timeReportData)
      .then((res) => res.data);
  };

  const updateStatus = async (status: ApiModel.UpdateStatusByDateRange) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportClient.UpdateStatusByDateRange(status)
      .then((res) => res.data);
  };

  const getDateRangeStatus = async (employeeId: number, start: Date, end: Date) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportClient.GetStatusByDateRange(employeeId, start, end)
      .then((res) => res.data);
  };

  const getCompTime = async (employeeId: number, year: number, month: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .TimeReportClient.GetCompensationTime(employeeId, year, month + 1)
      .then((res) => res.data);
  };

  const updateCompTime = async (comp: ApiModel.CompTimePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportClient.CreateCompTime(comp)
      .then((res) => res.data);
  };

  const getWeekDateDisplay = (weekSegment?: ApiModel.WeekSegment) => {
    if (weekSegment) {
      const startDate = new Date(weekSegment.weekSegmentStart);
      const endDate = new Date(weekSegment.weekSegmentEnd).getDate();
      return `${startDate.getDate()} - ${endDate} ${LocalizationHelperInstance.getFormattedDate(
        startDate,
        MonthShortConfig
      )}`;
    }
    return "";
  };

  const approveTimeReport = async (reportData: ApiModel.ReportData) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportClient.SubmitReport(reportData)
      .then((res) => res.data);
  };

  const getDisplayLockStatus = (status?: ApiModel.TimeReportStatus) => {
    if (status === ApiModel.TimeReportStatus.Open) {
      return true;
    }
    return false;
  };

  const canLoggedInUserChangeStatus = (status?: ApiModel.TimeReportStatus) => {
    if (status !== ApiModel.TimeReportStatus.Attested) {
      return true;
    }
    if (CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin, true)) {
      return true;
    }
    return false;
  };

  const getComments = async (projectTimeId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .TimeReportClient.Comments(projectTimeId)
      .then((res) => res.data);
  };

  return {
    hasCreatePermissions,
    hasEditPermissions,
    isProjectManager,
    getReport,
    getDueMonthReport,
    reportToAPIFilter,
    filter,
    exportReport,
    exportToAPIFilter,
    mergeWeeklyTimeReport,
    updateStatus,
    getDateRangeStatus,
    getCompTime,
    updateCompTime,
    getWeekDateDisplay,
    approveTimeReport,
    getDisplayLockStatus,
    canLoggedInUserChangeStatus,
    getComments,
  };
}

export default useTimeReportingService;
